import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTranslation, Trans } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Text } from '@paygreen/paygreen-ui';
import { useMode } from '../../../context';
import { postMatchingProduct } from '../service';
import AddManuallyProductForm from './AddManuallyProductForm';
import ProductModal from '../shared/ProductModal';

const AddManuallyProduct = ({
    handleRefreshData,
    setOpen,
    isOpen,
    t,
    setIsLoading,
    isLoading,
}) => {
    const { mode } = useMode();

    const [errorMessage, setErrorMessage] = useState(null);
    const [displayCategoryForm, setDisplayCategoryForm] = useState(false);

    // initializing all react-hook-form values and methods
    const defaultValues = {
        productExternalReference: '',
        productName: '',
        emissionExternalId: '',
        emissionFactorName: '',
        emissionFactorValue: '',
        monetaryRatioMatch: '',
        monetaryRatioName: '',
        monetaryRatioValue: '',
        documentation: '',
        source: '',
    };

    const methods = useForm({
        defaultValues: defaultValues,
        mode: 'onTouched',
        reValidateMode: 'onChange',
    });

    const { register, handleSubmit, watch, setValue, clearErrors } = methods;

    // watch specifically for emissionExternalId field without rerendering all form
    const selectedProduct =
        watch('emissionExternalId') || watch('monetaryRatioMatch')?.toString();

    // Watch for changes on inputs
    useEffect(() => {
        register({ name: 'productExternalReference' });
        register({ name: 'productName' });
        register({ name: 'emissionExternalId' });
        register({ name: 'monetaryRatioMatch' });
        register({ name: 'emissionFactorName' });
        register({ name: 'monetaryRatioName' });
        register({ name: 'emissionFactorValue' });
        register({ name: 'monetaryRatioValue' });
        register({ name: 'documentation' });
        register({ name: 'source' });
    }, [register]);

    const cancelCloseModal = useCallback(() => {
        setOpen(false);

        // we need the reset methods to wait until modal is closed
        setTimeout(() => {
            setErrorMessage();
            clearErrors();
            setDisplayCategoryForm(false);

            for (const [key] of Object.entries(defaultValues)) {
                setValue(key, '');
            }
        }, 500);
    }, [clearErrors, defaultValues, setOpen, setValue]);

    /**
     * Post request to API to create a new product
     * @param {object} newProduct - object with info to register new product to Climatekit database
     */
    const addMatchingProduct = useCallback(
        (newProduct) => {
            setIsLoading(true);

            postMatchingProduct(
                {
                    ...newProduct,
                    emissionExternalId: newProduct?.emissionExternalId || null,
                    monetaryRatioMatch: newProduct?.monetaryRatioMatch || null,
                },
                mode,
            )
                .then(() => {
                    handleRefreshData(true);

                    cancelCloseModal();

                    !isLoading &&
                        toast(
                            <Text colorPallet="theme" textSize="xs">
                                <Trans i18nKey="form.toastSuccess.addManuallyProduct">
                                    ...
                                    <Text
                                        htmlTag="strong"
                                        colorPallet="theme"
                                        textSize="xs"
                                    >
                                        {{
                                            productName:
                                                newProduct?.productName,
                                        }}
                                    </Text>
                                    ...
                                </Trans>
                            </Text>,
                        );
                })
                .catch((error) => {
                    const { detail, status } = error?.response?.data;

                    setErrorMessage(status + ' / ' + detail);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [handleRefreshData, setIsLoading, cancelCloseModal, isLoading, mode],
    );

    const submitForm = (data) => {
        setErrorMessage(null);

        const newProduct = {
            productExternalReference: data.productExternalReference,
            productName: data.productName,
            emissionExternalId: data.emissionExternalId,
            monetaryRatioMatch: data.monetaryRatioMatch,
        };

        addMatchingProduct(newProduct);
    };

    return (
        <FormProvider {...methods}>
            <ProductModal
                modalTitle={
                    !displayCategoryForm
                        ? t('matching.addManuallyForm.modalTitle')
                        : t('matching.addManuallyForm.modalEquivalentTitle')
                }
                blockWidth={displayCategoryForm ? 'xl' : 'md'}
                isOpen={isOpen}
                isLoading={isLoading}
                cancelCloseModal={cancelCloseModal}
                errorMessage={errorMessage}
                hasDisabledMode={displayCategoryForm}
            >
                <AddManuallyProductForm
                    onSubmit={handleSubmit(submitForm)}
                    defaultValues={defaultValues}
                    isOpen={isOpen}
                    displayCategoryForm={displayCategoryForm}
                    selectedProduct={selectedProduct}
                    cancelCloseModal={cancelCloseModal}
                />
            </ProductModal>
        </FormProvider>
    );
};

AddManuallyProduct.propTypes = {
    handleRefreshData: PropTypes.func,
    /**
     * @description setOpen and isOpen are automatically received from ModalControl parent
     */
    setOpen: PropTypes.func,
    isOpen: PropTypes.bool,
    setIsLoading: PropTypes.func,
};

AddManuallyProduct.defaultProps = {
    isOpen: false,
};

export default withTranslation('common')(AddManuallyProduct);
